import { Children, forwardRef, type ElementRef, type ReactNode } from 'react';
import {
  Button as TamaguiButton,
  GetProps,
  H2,
  Input as TamaguiInput,
  Spinner,
  Text as TamaguiText,
  TextArea as TamaguiTextArea,
  XStack,
  YStack,
  styled,
} from 'tamagui';

import {
  getAppButtonPresentationProps,
  getAppCardPresentationProps,
  getAppListRowPresentationProps,
  getAppPanelPresentationProps,
} from './presentation';
import type { AppPlatformPresentation, AppSurfaceDensity, AppSurfaceLevel } from './presentation';

export const AppPage = styled(YStack, {
  name: 'AppPage',
  width: '100%',
  maxWidth: 960,
  alignSelf: 'center',
  px: '$md',
  pt: '$sm',
  pb: '$xl',
  gap: '$md',
});

export const AppSection = styled(YStack, {
  name: 'AppSection',
  gap: '$sm',
});

export const AppRow = styled(XStack, {
  name: 'AppRow',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '$sm',
  width: '100%',
});

export const AppText = styled(TamaguiText, {
  name: 'AppText',
  fontFamily: '$body',
  color: '$color',
  fontSize: '$md',
  lineHeight: '$lg',
});

export function AppDisclosureGlyph({
  open,
}: {
  open: boolean;
}) {
  return (
    <AppText
      aria-hidden="true"
      color="$primary"
      fontSize="$xs"
      fontWeight="700"
      lineHeight="$xs"
      minWidth={14}
      textAlign="center"
    >
      {open ? '^' : 'v'}
    </AppText>
  );
}

export const AppHeading = styled(H2, {
  name: 'AppHeading',
  margin: 0,
  fontFamily: '$heading',
  color: '$color',
  fontSize: '$display',
  lineHeight: '$hero',
  letterSpacing: -0.6,
});

const AppCardFrame = styled(YStack, {
  name: 'AppCard',
  backgroundColor: '$cardBackground',
  borderColor: '$borderColor',
  borderWidth: 1,
  borderRadius: '$md',
  px: '$sm',
  py: 12,
  gap: '$xs',
  shadowColor: '$shadowColor',
  shadowOffset: { width: 0, height: 12 },
  shadowOpacity: 0.06,
  shadowRadius: 22,
  elevation: 2,
});

type AppCardProps = GetProps<typeof AppCardFrame> & {
  presentation?: AppPlatformPresentation;
  density?: AppSurfaceDensity;
  surface?: AppSurfaceLevel;
};

export const AppCard = forwardRef<ElementRef<typeof AppCardFrame>, AppCardProps>(
  function AppCard({
    children,
    presentation = 'shared',
    density = 'comfortable',
    surface = 'primary',
    borderColor,
    borderStyle,
    borderWidth,
    ...props
  }, ref) {
    const basePresentationProps = getAppCardPresentationProps(presentation, density, surface);
    const resolvesExplicitBorder =
      borderWidth !== undefined || borderColor !== undefined || borderStyle !== undefined;
    const resolvedBorderWidth =
      borderWidth !== undefined
        ? borderWidth
        : resolvesExplicitBorder
          ? 1
          : basePresentationProps.borderWidth;
    const resolvedChildren = Children.map(children, (child) => {
      if (typeof child === 'string') {
        return child.trim().length ? <AppText>{child}</AppText> : null;
      }

      if (typeof child === 'number') {
        return <AppText>{child}</AppText>;
      }

      return child;
    });

    return (
      <AppCardFrame
        ref={ref}
        {...basePresentationProps}
        borderWidth={resolvedBorderWidth}
        borderColor={borderColor ?? basePresentationProps.borderColor}
        borderStyle={borderStyle}
        {...props}
      >
        {resolvedChildren}
      </AppCardFrame>
    );
  },
);

export const AppCardHeader = styled(YStack, {
  name: 'AppCardHeader',
  gap: '$xs',
});

export const AppCardBody = styled(YStack, {
  name: 'AppCardBody',
  gap: '$xs',
});

export const AppCardFooter = styled(XStack, {
  name: 'AppCardFooter',
  alignItems: 'center',
  gap: '$sm',
  flexWrap: 'wrap',
});

export const AppButtonRow = styled(XStack, {
  name: 'AppButtonRow',
  alignItems: 'center',
  gap: '$sm',
  flexWrap: 'wrap',
});

export const AppFormSection = styled(YStack, {
  name: 'AppFormSection',
  width: '100%',
  gap: '$sm',
});

type AppButtonTone = 'primary' | 'secondary' | 'outline' | 'ghost' | 'destructive';
type AppButtonSize = 'sm' | 'md' | 'lg';
type AppButtonProps = Omit<GetProps<typeof TamaguiButton>, 'size'> & {
  tone?: AppButtonTone;
  size?: AppButtonSize;
  presentation?: AppPlatformPresentation;
  density?: AppSurfaceDensity;
};

const BUTTON_TONES: Record<AppButtonTone, Partial<GetProps<typeof TamaguiButton>>> = {
  primary: {
    backgroundColor: '$primary',
    borderColor: '$primary',
    color: '$primaryForeground',
  },
  secondary: {
    backgroundColor: '$secondary',
    borderColor: '$secondary',
    color: '$secondaryForeground',
  },
  outline: {
    backgroundColor: '$cardBackground',
    borderColor: '$borderColor',
    color: '$color',
  },
  ghost: {
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    color: '$primary',
  },
  destructive: {
    backgroundColor: '$destructive',
    borderColor: '$destructive',
    color: '$destructiveForeground',
  },
};

const BUTTON_SIZES: Record<AppButtonSize, Partial<GetProps<typeof TamaguiButton>>> = {
  sm: {
    height: '$xl',
    px: '$sm',
  },
  md: {
    height: '$hero',
    px: '$md',
  },
  lg: {
    height: 52,
    px: '$lg',
  },
};

export const AppButton = forwardRef<ElementRef<typeof TamaguiButton>, AppButtonProps>(
  function AppButton(
    {
      tone = 'primary',
      size = 'md',
      presentation = 'shared',
      density = 'comfortable',
      children,
      ...props
    },
    ref,
  ) {
    const resolvedTone: AppButtonTone = tone;
    const resolvedSize: AppButtonSize = size;
    const toneStyles = BUTTON_TONES[resolvedTone];
    const sizeStyles = BUTTON_SIZES[resolvedSize];
    const presentationStyles = getAppButtonPresentationProps(presentation, density);
    const textColor = (toneStyles.color as GetProps<typeof TamaguiText>['color'] | undefined) ?? '$color';
    const resolvedChildren = Children.map(children, (child) => {
      if (typeof child === 'string' || typeof child === 'number') {
        return (
          <AppText color={textColor} fontSize="$sm" fontWeight="600">
            {child}
          </AppText>
        );
      }

      return child;
    });

    return (
      <TamaguiButton
        ref={ref}
        unstyled
        borderWidth={tone === 'ghost' ? 0 : 1}
        borderRadius="$sm"
        alignItems="center"
        justifyContent="center"
        flexDirection="row"
        gap="$xs"
        fontFamily="$body"
        fontSize="$sm"
        fontWeight="600"
        pressStyle={{ opacity: 0.92 }}
        hoverStyle={resolvedTone === 'ghost' ? { opacity: 0.9 } : undefined}
        disabledStyle={{ opacity: 0.5 }}
        {...presentationStyles}
        {...sizeStyles}
        {...toneStyles}
        {...props}
      >
        {resolvedChildren}
      </TamaguiButton>
    );
  },
);

export function AppInput(props: GetProps<typeof TamaguiInput>) {
  const inputType = typeof props.type === 'string' ? props.type : undefined;
  const secureTextEntry =
    'secureTextEntry' in props
      ? props.secureTextEntry
      : inputType === 'password';
  const inputMode = props.inputMode ?? (inputType === 'number' ? 'decimal' : undefined);
  const autoComplete = props.autoComplete ?? (inputType === 'email' ? 'email' : undefined);
  const keyboardType = 'keyboardType' in props ? props.keyboardType : undefined;
  const shouldSpellCheck =
    props.spellCheck
    ?? !(
      inputType === 'email'
      || inputType === 'number'
      || inputType === 'password'
      || inputType === 'tel'
      || inputType === 'time'
      || inputType === 'url'
      || autoComplete === 'email'
      || autoComplete === 'postal-code'
      || autoComplete === 'one-time-code'
      || inputMode === 'decimal'
      || inputMode === 'email'
      || inputMode === 'numeric'
      || inputMode === 'tel'
      || inputMode === 'url'
      || keyboardType === 'decimal-pad'
      || keyboardType === 'email-address'
      || keyboardType === 'name-phone-pad'
      || keyboardType === 'number-pad'
      || keyboardType === 'numeric'
      || keyboardType === 'phone-pad'
      || keyboardType === 'url'
    );
  const autoCorrect = props.autoCorrect ?? shouldSpellCheck;
  const autoCapitalize =
    props.autoCapitalize
    ?? (
      inputType === 'email'
      || inputType === 'number'
      || inputType === 'password'
      || inputType === 'tel'
      || inputType === 'time'
      || inputType === 'url'
      || autoComplete === 'postal-code'
      || inputMode === 'decimal'
      || inputMode === 'email'
      || inputMode === 'numeric'
      || inputMode === 'tel'
      || inputMode === 'url'
      || keyboardType === 'decimal-pad'
      || keyboardType === 'email-address'
      || keyboardType === 'name-phone-pad'
      || keyboardType === 'number-pad'
      || keyboardType === 'numeric'
      || keyboardType === 'phone-pad'
      || keyboardType === 'url'
        ? 'none'
        : undefined
    );

  return (
    <TamaguiInput
      height="$hero"
      px="$sm"
      py="$xs"
      fontFamily="$body"
      fontSize="$md"
      color="$color"
      backgroundColor="$inputBackground"
      borderColor="$inputBorderColor"
      borderWidth={1}
      borderRadius="$sm"
      focusStyle={{ borderColor: '$primary' }}
      inputMode={inputMode}
      autoComplete={autoComplete}
      spellCheck={shouldSpellCheck}
      autoCorrect={autoCorrect}
      autoCapitalize={autoCapitalize}
      secureTextEntry={secureTextEntry}
      {...props}
    />
  );
}

export function AppTextArea(props: GetProps<typeof TamaguiTextArea>) {
  const { className, spellCheck, autoCorrect, ...rest } = props;

  return (
    <TamaguiTextArea
      minHeight={112}
      px="$sm"
      py="$sm"
      fontFamily="$body"
      fontSize="$md"
      color="$color"
      backgroundColor="$inputBackground"
      borderColor="$inputBorderColor"
      borderWidth={1}
      borderRadius="$sm"
      focusStyle={{ borderColor: '$primary' }}
      className={['resize-none', className].filter(Boolean).join(' ')}
      spellCheck={spellCheck ?? true}
      autoCorrect={autoCorrect ?? true}
      {...rest}
    />
  );
}

type AppChipFrameProps = GetProps<typeof TamaguiButton> & {
  selected?: boolean;
};

export function AppChipFrame({
  selected = false,
  children,
  ...props
}: AppChipFrameProps) {
  return (
    <TamaguiButton
      unstyled
      borderWidth={1}
      borderColor={selected ? '$primary' : '$borderColor'}
      borderRadius="$pill"
      backgroundColor={selected ? '$primaryTint' : '$mutedBackground'}
      px="$sm"
      py="$xs"
      pressStyle={{ opacity: 0.92 }}
      {...props}
    >
      {children}
    </TamaguiButton>
  );
}

type AppChipTextProps = GetProps<typeof TamaguiText> & {
  selected?: boolean;
};

export function AppChipText({
  selected = false,
  children,
  ...props
}: AppChipTextProps) {
  return (
    <TamaguiText
      color={selected ? '$primary' : '$colorMuted'}
      fontFamily="$body"
      fontSize="$sm"
      fontWeight="600"
      {...props}
    >
      {children}
    </TamaguiText>
  );
}

type AppPanelFrameProps = GetProps<typeof TamaguiButton> & {
  selected?: boolean;
  presentation?: AppPlatformPresentation;
  density?: AppSurfaceDensity;
};

export function AppPanelFrame({
  selected = false,
  presentation = 'shared',
  density = 'comfortable',
  children,
  ...props
}: AppPanelFrameProps) {
  const presentationStyles = getAppPanelPresentationProps(presentation, density);

  return (
    <TamaguiButton
      unstyled
      alignItems="stretch"
      justifyContent="flex-start"
      {...presentationStyles}
      borderWidth={1}
      borderColor={selected ? '$primary' : '$borderColor'}
      backgroundColor={selected ? '$primaryTint' : '$mutedBackground'}
      pressStyle={{ opacity: 0.92 }}
      {...props}
    >
      {children}
    </TamaguiButton>
  );
}

export const AppPanelStack = styled(YStack, {
  name: 'AppPanelStack',
  flex: 1,
  gap: '$xs',
  justifyContent: 'flex-start',
});

type AppPanelTextProps = GetProps<typeof TamaguiText> & {
  selected?: boolean;
};

export function AppPanelTitle({
  selected = false,
  children,
  ...props
}: AppPanelTextProps) {
  return (
    <TamaguiText
      color={selected ? '$primary' : '$color'}
      fontFamily="$body"
      fontSize="$md"
      fontWeight="700"
      lineHeight={20}
      {...props}
    >
      {children}
    </TamaguiText>
  );
}

export function AppPanelSubtitle({
  selected = false,
  children,
  ...props
}: AppPanelTextProps) {
  return (
    <TamaguiText
      color={selected ? '$primary' : '$colorMuted'}
      fontFamily="$body"
      fontSize="$sm"
      lineHeight={18}
      {...props}
    >
      {children}
    </TamaguiText>
  );
}

export function AppPanelMeta({
  selected = false,
  children,
  ...props
}: AppPanelTextProps) {
  return (
    <TamaguiText
      color={selected ? '$primary' : '$colorMuted'}
      fontFamily="$body"
      fontSize="$xs"
      fontWeight="700"
      lineHeight={16}
      marginTop="auto"
      {...props}
    >
      {children}
    </TamaguiText>
  );
}

export function AppGuideCardButton(props: GetProps<typeof TamaguiButton>) {
  return (
    <TamaguiButton
      unstyled
      alignItems="stretch"
      justifyContent="flex-start"
      borderWidth={1}
      borderColor="$primary"
      borderRadius="$xl"
      backgroundColor="$primary"
      minHeight={132}
      px="$sm"
      py={12}
      gap="$xs"
      shadowColor="$shadowColor"
      shadowOffset={{ width: 0, height: 12 }}
      shadowOpacity={0.12}
      shadowRadius={24}
      elevation={3}
      pressStyle={{ opacity: 0.94 }}
      {...props}
    />
  );
}

export const AppGuideCardHeader = styled(XStack, {
  name: 'AppGuideCardHeader',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '$sm',
});

export const AppGuideCardStack = styled(YStack, {
  name: 'AppGuideCardStack',
  flex: 1,
  justifyContent: 'space-between',
  gap: '$sm',
});

export const AppGuideCardIcon = styled(TamaguiText, {
  name: 'AppGuideCardIcon',
  fontSize: '$xxl',
  lineHeight: '$xxl',
});

export const AppGuideCardTextStack = styled(YStack, {
  name: 'AppGuideCardTextStack',
  flex: 1,
  minWidth: 0,
  gap: 4,
});

export const AppGuideCardTitle = styled(TamaguiText, {
  name: 'AppGuideCardTitle',
  color: '$color',
  fontFamily: '$body',
  fontSize: '$md',
  fontWeight: '700',
  lineHeight: 21,
});

export const AppGuideCardDescription = styled(TamaguiText, {
  name: 'AppGuideCardDescription',
  color: '$colorMuted',
  fontFamily: '$body',
  fontSize: '$sm',
  lineHeight: 18,
});

export const AppGuideMetaRow = styled(XStack, {
  name: 'AppGuideMetaRow',
  flexWrap: 'wrap',
  gap: '$xs',
});

export const AppGuideMetaChip = styled(XStack, {
  name: 'AppGuideMetaChip',
  alignItems: 'center',
  borderWidth: 1,
  borderColor: '$borderColor',
  borderRadius: '$pill',
  backgroundColor: '$cardBackground',
  px: 12,
  py: 6,
});

export const AppGuideMetaText = styled(TamaguiText, {
  name: 'AppGuideMetaText',
  color: '$colorMuted',
  fontFamily: '$body',
  fontSize: '$xs',
  lineHeight: '$sm',
});

export const AppGuideStepBadge = styled(XStack, {
  name: 'AppGuideStepBadge',
  width: 32,
  height: 32,
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '$pill',
  backgroundColor: '$primaryTint',
});

export const AppGuideStepBadgeText = styled(TamaguiText, {
  name: 'AppGuideStepBadgeText',
  color: '$primary',
  fontFamily: '$body',
  fontSize: '$xs',
  fontWeight: '700',
  lineHeight: '$sm',
});

type AppAvatarFrameProps = GetProps<typeof YStack> & {
  size?: number;
};

export function AppAvatarFrame({
  size = 56,
  children,
  ...props
}: AppAvatarFrameProps) {
  return (
    <YStack
      width={size}
      height={size}
      borderRadius={size / 2}
      alignItems="center"
      justifyContent="center"
      backgroundColor="$primaryTint"
      borderColor="$borderColor"
      borderWidth={1}
      overflow="hidden"
      {...props}
    >
      {children}
    </YStack>
  );
}

type AppAvatarFallbackTextProps = GetProps<typeof TamaguiText> & {
  size?: number;
};

export function AppAvatarFallbackText({
  size = 56,
  children,
  ...props
}: AppAvatarFallbackTextProps) {
  return (
    <TamaguiText
      color="$primary"
      fontFamily="$body"
      fontWeight="700"
      fontSize={Math.max(16, Math.round(size * 0.36))}
      {...props}
    >
      {children}
    </TamaguiText>
  );
}

export const AppMetricCardFrame = styled(YStack, {
  name: 'AppMetricCardFrame',
  backgroundColor: '$cardBackground',
  borderColor: '$borderColor',
  borderWidth: 1,
  borderRadius: '$md',
  flex: 1,
  minWidth: 0,
  px: '$sm',
  py: '$sm',
  gap: '$xs',
});

export const AppMetricCardLabel = styled(TamaguiText, {
  name: 'AppMetricCardLabel',
  color: '$colorMuted',
  fontFamily: '$body',
  fontSize: 11,
  fontWeight: '700',
  textAlign: 'center',
  textTransform: 'uppercase',
});

export const AppMetricCardValue = styled(TamaguiText, {
  name: 'AppMetricCardValue',
  color: '$color',
  fontFamily: '$body',
  fontSize: '$lg',
  fontWeight: '800',
  textAlign: 'center',
});

const AppListRowFrameBase = styled(XStack, {
  name: 'AppListRowFrame',
  width: '100%',
  alignItems: 'flex-start',
  gap: '$sm',
  py: '$sm',
  borderBottomWidth: 1,
  borderBottomColor: '$borderColor',
});

type AppListRowFrameProps = GetProps<typeof AppListRowFrameBase> & {
  presentation?: AppPlatformPresentation;
  density?: AppSurfaceDensity;
};

export const AppListRowFrame = forwardRef<ElementRef<typeof AppListRowFrameBase>, AppListRowFrameProps>(
  function AppListRowFrame({ presentation = 'shared', density = 'comfortable', ...props }, ref) {
    return (
      <AppListRowFrameBase
        ref={ref}
        {...getAppListRowPresentationProps(presentation, density)}
        {...props}
      />
    );
  },
);

export const AppListRowTextStack = styled(YStack, {
  name: 'AppListRowTextStack',
  flex: 1,
  minWidth: 0,
});

export const AppListRowTitle = styled(TamaguiText, {
  name: 'AppListRowTitle',
  color: '$color',
  fontFamily: '$body',
  fontSize: 15,
  fontWeight: '700',
});

export const AppListRowMeta = styled(TamaguiText, {
  name: 'AppListRowMeta',
  color: '$colorMuted',
  fontFamily: '$body',
  fontSize: 13,
  lineHeight: 19,
  mt: 4,
});

export function AppNudgeButton(props: GetProps<typeof TamaguiButton>) {
  return (
    <TamaguiButton
      unstyled
      alignItems="center"
      flexDirection="row"
      justifyContent="space-between"
      width="100%"
      px={0}
      pressStyle={{ opacity: 0.92 }}
      {...props}
    />
  );
}

export const AppNudgeText = styled(TamaguiText, {
  name: 'AppNudgeText',
  flex: 1,
  color: '$primary',
  fontFamily: '$body',
  fontSize: 13,
  lineHeight: 18,
  fontWeight: '500',
});

export const AppNudgeArrow = styled(TamaguiText, {
  name: 'AppNudgeArrow',
  color: '$primary',
  fontFamily: '$body',
  fontSize: 16,
  fontWeight: '600',
  ml: '$sm',
});

export function AppActionPillButton(props: GetProps<typeof TamaguiButton>) {
  return (
    <TamaguiButton
      unstyled
      alignItems="center"
      justifyContent="center"
      backgroundColor="$primary"
      borderRadius="$pill"
      px="$md"
      py={8}
      pressStyle={{ opacity: 0.92 }}
      {...props}
    />
  );
}

export const AppActionPillText = styled(TamaguiText, {
  name: 'AppActionPillText',
  color: '$primaryForeground',
  fontFamily: '$body',
  fontSize: 13,
  lineHeight: 18,
  fontWeight: '700',
});

export function AppTextActionButton(props: GetProps<typeof TamaguiButton>) {
  return (
    <TamaguiButton
      unstyled
      py={8}
      pressStyle={{ opacity: 0.92 }}
      {...props}
    />
  );
}

export const AppTextActionText = styled(TamaguiText, {
  name: 'AppTextActionText',
  color: '$primary',
  fontFamily: '$body',
  fontSize: 13,
  lineHeight: 18,
  fontWeight: '600',
});

export function AppSuggestionCardButton(props: GetProps<typeof TamaguiButton>) {
  return (
    <TamaguiButton
      unstyled
      alignItems="stretch"
      backgroundColor="$background"
      borderColor="$borderColor"
      borderWidth={1}
      borderRadius="$lg"
      gap={6}
      px="$sm"
      py="$sm"
      pressStyle={{ opacity: 0.94 }}
      {...props}
    />
  );
}

type AppFieldGroupProps = {
  label?: string;
  helper?: string;
  error?: string;
  children: ReactNode;
};

export function AppFieldGroup({ label, helper, error, children }: AppFieldGroupProps) {
  return (
    <AppFormSection gap="$xs">
      {label ? (
        <AppText fontSize="$sm" fontWeight="700">
          {label}
        </AppText>
      ) : null}
      {children}
      {error ? (
        <AppText color="$destructive" fontSize="$sm">
          {error}
        </AppText>
      ) : helper ? (
        <AppText color="$colorMuted" fontSize="$sm">
          {helper}
        </AppText>
      ) : null}
    </AppFormSection>
  );
}

type AppSectionHeaderProps = {
  title: string;
  subtitle?: string;
  action?: ReactNode;
};

export function AppSectionHeader({ title, subtitle, action }: AppSectionHeaderProps) {
  return (
    <AppRow alignItems="flex-start">
      <YStack flex={1} gap="$xs">
        <AppText fontSize="$md" lineHeight={22} fontWeight="700">
          {title}
        </AppText>
        {subtitle ? <AppText color="$colorMuted" fontSize="$sm" lineHeight={20}>{subtitle}</AppText> : null}
      </YStack>
      {action}
    </AppRow>
  );
}

type AppInlineAlertTone = 'info' | 'success' | 'warning' | 'error';
type AppInlineAlertProps = {
  title: string;
  children: ReactNode;
  tone?: AppInlineAlertTone;
};

const ALERT_TONES: Record<AppInlineAlertTone, Partial<GetProps<typeof AppCard>>> = {
  info: {
    backgroundColor: '$secondaryTint',
  },
  success: {
    backgroundColor: '$successTint',
  },
  warning: {
    backgroundColor: '$accentTint',
  },
  error: {
    backgroundColor: '$destructiveTint',
  },
};

export function AppInlineAlert({ title, children, tone = 'info' }: AppInlineAlertProps) {
  return (
    <AppCard density="compact" surface="secondary" gap="$xs" {...ALERT_TONES[tone]}>
      <AppText fontSize="$sm" fontWeight="700">
        {title}
      </AppText>
      <AppText fontSize="$sm">{children}</AppText>
    </AppCard>
  );
}

type AppStateProps = {
  title: string;
  body: string;
  action?: ReactNode;
};

export function AppEmptyState({ title, body, action }: AppStateProps) {
  return (
    <AppCard alignItems="center" py="$lg">
      <AppText fontSize="$md" lineHeight={22} fontWeight="700" textAlign="center">
        {title}
      </AppText>
      <AppText color="$colorMuted" fontSize="$sm" lineHeight={20} textAlign="center">
        {body}
      </AppText>
      {action}
    </AppCard>
  );
}

export function AppLoadingState({ title, body, action }: AppStateProps) {
  return (
    <AppCard alignItems="center" py="$lg">
      <Spinner color="$primary" />
      <AppText fontSize="$lg" fontWeight="700" textAlign="center">
        {title}
      </AppText>
      <AppText color="$colorMuted" textAlign="center">
        {body}
      </AppText>
      {action}
    </AppCard>
  );
}

export function AppErrorState({ title, body, action }: AppStateProps) {
  return (
    <AppCard alignItems="center" py="$lg" borderColor="$destructive">
      <AppText fontSize="$lg" fontWeight="700" textAlign="center">
        {title}
      </AppText>
      <AppText color="$colorMuted" textAlign="center">
        {body}
      </AppText>
      {action}
    </AppCard>
  );
}
